import { useGetDossierOverzicht } from "@/entities/dossier-overzicht";
import { RoleGate, UserRole } from "@/features/auth";
import { useDossierPinStore } from "@/features/dossier-management";
import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components";
import { createContext, ReactNode, useContext, useState } from "react";
import { CloseDossierDialog } from "./close-dossier/close-dossier-dialog";

type DossierToolbarContextType = {
    dossierNummer: string;
};
const DossierToolbarContext = createContext<DossierToolbarContextType | null>(null);

const useDossierToolbarContext = () => {
    const context = useContext(DossierToolbarContext);
    if (!context) {
        throw new Error("Expected a DossierToolbarContext");
    }
    return context;
};

type DossierToolbarProps = {
    dossierNummer: string;
    children: ReactNode;
};

export function DossierToolbar({ dossierNummer, children }: DossierToolbarProps) {
    return (
        <DossierToolbarContext value={{ dossierNummer }}>
            <div className="flex items-center justify-end gap-2">{children}</div>
        </DossierToolbarContext>
    );
}

function BeeindigDossierButton() {
    const { dossierNummer } = useDossierToolbarContext();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <RoleGate roles={[UserRole.REGIEVOERDER]}>
            <Button
                className="min-h-min grow-0"
                icon="check-circle"
                iconStrokeWidth="thick"
                iconSize="xs"
                variant="secondary"
                size="s"
                onClick={() => setIsOpen(true)}
            >
                Dossier beëindigen
            </Button>
            {isOpen && <CloseDossierDialog dossierNummer={dossierNummer} open={isOpen} onOpenChange={setIsOpen} />}
        </RoleGate>
    );
}

function PinDossierButton() {
    const { dossierNummer } = useDossierToolbarContext();
    const dossierId = Number(dossierNummer);
    const { data: openDossiers } = useGetDossierOverzicht();
    const openDossierIds = openDossiers?.map((d) => d.dossierId) ?? [];
    const isOpenDossier = openDossierIds.includes(dossierId);
    const isPinned = useDossierPinStore((s) => s.isPinned(dossierId));
    const togglePin = useDossierPinStore((s) => s.togglePin);
    const label = isPinned ? "Losmaken" : "Vastzetten";

    if (!isOpenDossier) return null;

    return (
        <RoleGate roles={[UserRole.REGIEVOERDER]}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        iconOnly
                        icon={isPinned ? "pin-filled" : "pin"}
                        variant="tertiary"
                        aria-label={label}
                        size="s"
                        onClick={() => togglePin(dossierId, openDossierIds)}
                    />
                </TooltipTrigger>
                <TooltipContent>{label}</TooltipContent>
            </Tooltip>
        </RoleGate>
    );
}

DossierToolbar.Pin = PinDossierButton;
DossierToolbar.BeeindigDossier = BeeindigDossierButton;
